'use client';
import * as React from 'react';

import { Skeleton, Typography } from '@mui/material';
import LessonItem from './lesson-item';
import { useLearningPathByIdStore } from '../../store/learning-path-by-id-store';
import { EventBus } from '@/shared/utils';
import { LearningPathByIdEventKeys, LearningPathByIdEvents } from '../../events';
import { NumberOfLessons } from './number-of-lessons';
import { formatDuration } from '../../utils/return-duration-from-seconds';
import { getVideoCompletionPercentage } from '../../utils/return-video-completion-percentage';
import { sortLessonsByFree } from '../../utils/return-sorted-by-free-lessons';
import DetailsLessonsSkeleton from 'src/shared/components/loading-skeleton/details-lesson';

interface LessonListProps {
  learningPathDocumentId: string;
  learningPathName: string;
  eventBus: EventBus<LearningPathByIdEvents>;
  isSubscriber: boolean;
}

const LessonList: React.FC<LessonListProps> = ({
  learningPathDocumentId,
  learningPathName,
  eventBus,
  isSubscriber,
}) => {
  const { lessons, customerSubscription } = useLearningPathByIdStore();
  const [currentLessonIndex, setCurrentLessonIndex] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!lessons?.data)
      eventBus.emit(LearningPathByIdEventKeys.lessons.view, {
        learningPathDocumentId,
      });
    // assign first active lesson once customer subscription is fetched
    if (!customerSubscription?.data) return;
    // set first active default element once lessons are fetched
    if (!!lessons?.data && !currentLessonIndex) {
      const firstActiveLesson = lessons?.data?.find(
        (el) =>
          el?.lessonProgresses?.[0]?.progress_status !== 'completed' && (el?.free || isSubscriber),
      );
      setCurrentLessonIndex(firstActiveLesson?.documentId || null);
    }
  }, [customerSubscription?.data, lessons?.data, eventBus, currentLessonIndex, isSubscriber]);
  if (lessons.status.isLoading || !lessons?.data || !customerSubscription?.data)
    return <DetailsLessonsSkeleton />;

  const orderedLessons = isSubscriber ? lessons?.data : sortLessonsByFree(lessons?.data || []);
  return (
    <>
      <div className="flex justify-between items-center w-full mb-6">
        <Typography variant="h3">Lezioni</Typography>
        <Typography variant="s1">
          <NumberOfLessons /> elementi
        </Typography>
      </div>
      <section className="space-y-3">
        {orderedLessons?.map((lesson) => {
          /* 
            Lessons have 4 UI states: 
            - locked (can't be click, user is not subscribed and the lesson is not free)
            - in progress (shows a progress value based on the timestamp, is still clickable)
            - watched (marked as completed, is still clickable)
            - not watched (no ui feedback, is clickable)
            - current active (is the current selected lesson, mark as read is only available if isSubscriber)
          */
          const isCurrent = lesson.documentId === currentLessonIndex;
          const lessonProgress = getVideoCompletionPercentage(
            lesson?.duration || 0,
            lesson?.lessonProgresses?.[0]?.current_second || 0,
          );
          const isWatched =
            lesson?.lessonProgresses?.[0]?.progress_status === 'completed' ||
            lessonProgress === 100 ||
            false;
          return (
            <LessonItem
              key={lesson.documentId}
              eventBus={eventBus}
              learningPathName={learningPathName}
              learningPathDocumentId={learningPathDocumentId}
              documentId={lesson.documentId}
              playbackId={lesson.playbackId}
              instructors={
                lesson.teacherProfiles?.map((t) => ({
                  name: t?.displayName || '-',
                  avatarUrl: t?.profilePicture?.url || '',
                  profession: t?.mainSubject || '',
                })) || []
              }
              title={lesson?.title || '-'}
              lessonProgress={lessonProgress}
              lessonProgressesDocumentId={lesson?.lessonProgresses?.[0]?.documentId || ''}
              description={lesson?.description || ''}
              isSubscriber={isSubscriber}
              duration={lesson?.duration ?? 0}
              isWatched={isWatched}
              isLocked={(!isSubscriber && !lesson?.free) || false}
              imageSrc={lesson?.thumbnail?.url || ''}
              isCurrent={isCurrent}
              onClickCallback={(id) => {
                setCurrentLessonIndex(id);
              }}
              openModalCallback={(type, data) => {
                if (type === 'video-modal') {
                  eventBus.emit(LearningPathByIdEventKeys.modals.videoModal, data);
                  return;
                }
                eventBus.emit(LearningPathByIdEventKeys.modals.lessonCompletionModal, data);
              }}
            />
          );
        })}
      </section>
    </>
  );
};

export default LessonList;
